import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Building2, Mail, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail, roleHome } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/pages/auth/AuthShell";

export default function Activate() {
  const { t } = useTranslation();
  const { token } = useParams();
  const nav = useNavigate();
  const { activateInvite } = useAuth();
  const [state, setState] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [invalidMsg, setInvalidMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}/validate`);
        setInvite(data);
        setState("valid");
      } catch (err) {
        setInvalidMsg(formatApiErrorDetail(err.response?.data?.detail) || "This invitation is not valid.");
        setState("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("register.mismatch")); return; }
    if (password.length < 8) { setError(t("register.tooShort")); return; }
    if (!terms || !privacy) { setError(t("register.mustAccept")); return; }
    setSubmitting(true);
    try {
      const u = await activateInvite({ token, password, accept_terms: terms, accept_privacy: privacy });
      toast.success(t("activate.active"));
      nav(roleHome(u?.role || "EMPLOYEE"), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading")
    return <AuthShell title={t("activate.verifying")} subtitle={t("activate.wait")} testid="activate-loading"><div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-cyan-dark" /></div></AuthShell>;

  if (state === "invalid")
    return (
      <AuthShell title={t("activate.invalidTitle")} testid="activate-invalid" footer={<Link to="/login" className="font-semibold text-cyan-dark hover:underline">{t("login.title")}</Link>}>
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-center" data-testid="activate-invalid-box">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
          <p className="mt-3 text-sm text-slate-600">{invalidMsg}</p>
          <p className="mt-2 text-xs text-slate-400">{t("activate.contact")}</p>
        </div>
      </AuthShell>
    );

  return (
    <AuthShell title={t("activate.title")} subtitle={t("activate.subtitle")} testid="activate-page">
      <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-semibold text-navy">{invite.first_name} {invite.last_name}</div>
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-600"><Building2 className="h-4 w-4 text-cyan-dark" /> {invite.company}</div>
        <div className="mt-1 flex items-center gap-2 text-sm text-slate-600"><Mail className="h-4 w-4 text-cyan-dark" /> {invite.email}</div>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2"><Label>{t("activate.create")}</Label><PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="activate-password" /></div>
        <div className="space-y-2"><Label>{t("activate.confirm")}</Label><PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="activate-confirm" /></div>
        <div className="space-y-3 rounded-xl bg-slate-50 p-4">
          <label className="flex items-start gap-3 text-sm text-slate-600"><Checkbox checked={terms} onCheckedChange={setTerms} data-testid="activate-terms" className="mt-0.5" /> {t("activate.terms")}</label>
          <label className="flex items-start gap-3 text-sm text-slate-600"><Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="activate-privacy" className="mt-0.5" /> {t("activate.privacy")}</label>
        </div>
        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="activate-error">{error}</div>}
        <Button type="submit" disabled={submitting} data-testid="activate-submit-button" className="w-full rounded-full bg-cyan text-white hover:bg-cyan-dark">
          {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("activate.activating")}</> : <><ShieldCheck className="mr-2 h-4 w-4" /> {t("activate.submit")}</>}
        </Button>
      </form>
    </AuthShell>
  );
}
